import type { DensitySnapshot, LockedCard } from "@screener/protocol";
import { useEffect, useMemo, useState } from "react";
import type { DensityCard } from "./cards";
import {
  applyFreeze,
  dropCoin,
  type FreezeDeps,
  type FrozenCards,
  freezeCards,
  hiddenCountsByKey,
  isFreezeCurrent,
  setPinnedFlags,
} from "./cards-freeze";
import { composeFrozenGrid, type GridCard, gridLayout, mergeLockedCards } from "./locked-cards";
import { useStableCallback } from "./use-stable";

/**
 * Состояние заморозки вида «Карточки» §77 (design stage9-cards-freeze Д4).
 * Нигде не сохраняется: после перезагрузки и при входе в вид экран не
 * заморожен. Снимок запоминает вместе со входами, при которых его сняли;
 * смена любого входа (`isFreezeCurrent`) размораживает вид прямо в рендере,
 * без кадра со старым расположением.
 */

interface CardsFreezeOptions {
  /** Входы, смена любого размораживает вид: сортировка, фильтры, минимумы, порядок строк, вид. */
  deps: FreezeDeps;
  /** Карточки, как их строит вид сейчас: из них снимается расположение в момент нажатия. */
  buildCurrent: () => DensityCard[];
  /** Закрытые карточки тарифа после фильтров экрана: на момент нажатия входят в снимок. */
  lockedNow: readonly LockedCard[];
  /** Все живые плотности потока до фильтров экрана (design Д2): по ним решается, жива ли строка. */
  liveRows: readonly DensitySnapshot[];
  /** Плотности после фильтров: по ним считается счёт новых. */
  visibleRows: readonly DensitySnapshot[];
  /** Скрытые монеты §75: их карточки уходят из снимка сразу, заморозка остаётся. */
  hidden: ReadonlySet<string>;
  /** Ключи закреплённых карточек: флаг в снимке следует за ними, без перестановки. */
  pinnedKeys: ReadonlySet<string>;
}

export interface CardsFreeze {
  frozen: boolean;
  newCount: number;
  /** Карточки замороженного вида; `null` — вид не заморожен. */
  cards: DensityCard[] | null;
  /** Сетка замороженного вида вместе с закрытыми карточками снимка; `null` — вид не заморожен. */
  grid: GridCard[] | null;
  toggle: () => void;
}

interface FreezeState {
  frozen: FrozenCards;
  deps: FreezeDeps;
  /** Закрытые карточки на момент нажатия: пока вид заморожен, их изменения не применяются. */
  locked: readonly LockedCard[];
  /** Порядок сетки на момент нажатия, включая закрытые карточки. */
  layout: readonly string[];
  /** Последний живой снимок строк, виденный после нажатия. */
  lastSeen: ReadonlyMap<string, DensitySnapshot>;
}

export function useCardsFreeze({
  deps,
  buildCurrent,
  lockedNow,
  liveRows,
  visibleRows,
  hidden,
  pinnedKeys,
}: CardsFreezeOptions): CardsFreeze {
  const [state, setState] = useState<FreezeState | null>(null);
  const active = state !== null && isFreezeCurrent(state.deps, deps) ? state : null;

  // Устаревшее состояние (вход сменился) не должно ожить, если вход вернётся
  // к прежней ссылке, — поэтому его сбрасывают, а не только игнорируют.
  useEffect(() => {
    if (state !== null && active === null) {
      setState(null);
    }
  }, [state, active]);

  // Скрытие монеты убирает её карточки сразу (§77 п. 5). Рендер уже не
  // показывает их (`withoutHidden` ниже), эффект закрепляет это в снимке,
  // чтобы возврат монеты не воскрешал карточку.
  useEffect(() => {
    if (hidden.size === 0) {
      return;
    }
    setState((previous) => {
      if (previous === null) {
        return previous;
      }
      let next = previous.frozen;
      for (const base of hidden) {
        next = dropCoin(next, base);
      }
      return next === previous.frozen ? previous : { ...previous, frozen: next };
    });
  }, [hidden]);

  const view = useMemo(() => {
    if (active === null) {
      return null;
    }
    let frozen = active.frozen;
    for (const base of hidden) {
      frozen = dropCoin(frozen, base);
    }
    frozen = setPinnedFlags(frozen, pinnedKeys, hiddenCountsByKey(liveRows, visibleRows));
    const liveById = new Map(liveRows.map((row) => [row.density_id, row]));
    const applied = applyFreeze(
      frozen,
      liveById,
      visibleRows.map((row) => row.density_id),
      active.lastSeen,
    );
    const grid = composeFrozenGrid(applied.cards, active.locked, active.layout);
    return { ...applied, grid, source: active.frozen };
  }, [active, hidden, pinnedKeys, liveRows, visibleRows]);

  // Последний живой снимок каждой строки после нажатия: ушедшая строка
  // показывает его, а не снимок момента нажатия. Лежит в состоянии, а не в
  // ссылке, — рендер не читает ничего, что правится мимо него. Состояние
  // обновляется, только пока есть новые снимки: когда все уже учтены,
  // `setState` возвращает прежнее значение и цикла рендеров нет.
  useEffect(() => {
    if (view === null) {
      return;
    }
    setState((previous) => {
      // Снимок расположения сменился (скрытие монеты, новая заморозка) — этот
      // вид устарел, следующий рендер посчитает свой.
      if (previous === null || previous.frozen !== view.source) {
        return previous;
      }
      let next: Map<string, DensitySnapshot> | null = null;
      for (const card of view.cards) {
        for (const row of card.rows) {
          if (
            card.endedIds?.has(row.density_id) !== true &&
            previous.lastSeen.get(row.density_id) !== row
          ) {
            next ??= new Map(previous.lastSeen);
            next.set(row.density_id, row);
          }
        }
      }
      return next === null ? previous : { ...previous, lastSeen: next };
    });
  }, [view]);

  const toggle = useStableCallback(() => {
    if (active !== null) {
      setState(null);
      return;
    }
    const current = buildCurrent();
    setState({
      frozen: freezeCards(current),
      deps,
      locked: lockedNow,
      layout: gridLayout(mergeLockedCards(current, lockedNow, deps.sort)),
      lastSeen: new Map(),
    });
  });

  return {
    frozen: view !== null,
    newCount: view?.newCount ?? 0,
    cards: view?.cards ?? null,
    grid: view?.grid ?? null,
    toggle,
  };
}
